import { Navigate, Route, Routes, useParams } from "react-router-dom";
import {
  ThemeProvider,
  createTheme as muiCreateTheme,
  StyledEngineProvider,
} from "@mui/material/styles";
import { einsteinTheme } from "@tiny/theme/createTheme";
import { TinyApiProvider } from "@tiny/api";
import {
  MessagesProvider,
  ConfirmProvider,
  RefreshProvider,
  Confirm,
  createTheme,
} from "@tiny/components";
import {
  AudienceHome,
  AudienceSourceAll,
  AudienceSourceDetails,
  EditSegment,
  ViewSegment,
  SuppressionSourceDetails,
} from "@tiny/audience";
import AlbertHostProvider from "./host/AlbertHostProvider";
import { tinyApiClient } from "./host/tinyApi";

/**
 * TinyAlbert's audience feature — the shared @tiny/audience pages mounted behind
 * the host contract. Wraps the subtree in the providers those pages depend on
 * (API client, messages, confirm, refresh, TinyHost) and routes the sub-pages
 * with React Router v6. Registered at `/main/audience/*`.
 *
 * The page components stay router-free and receive URL params as props, so the
 * param extraction happens here in thin wrappers.
 */

function SourceDetailsRoute() {
  const { segmentId } = useParams();
  return <AudienceSourceDetails segmentId={segmentId} />;
}

function SuppressionDetailsRoute() {
  const { suppressionId } = useParams();
  return <SuppressionSourceDetails suppressionId={suppressionId} />;
}

function EditSegmentRoute() {
  const { segmentId } = useParams();
  return <EditSegment segmentId={segmentId} />;
}

function ViewSegmentRoute() {
  const { segmentId } = useParams();
  return <ViewSegment segmentId={segmentId} />;
}

// The shared @tiny/audience components require @tiny/components' theme STRUCTURE
// (standard MUI grey keys 50–900, `body1-3` sizing, MuiLink/CssBaseline
// overrides) — under einstein's own theme they render broken/"alien" because its
// grey keys are 0–100. But that base carries TinyEmail's BRAND colors, so we
// overlay all of TinyAlbert's brand colors so the audience looks native to
// einstein. (The grey *values* are identical between the two design systems, so
// they don't need overriding.)
const e = einsteinTheme.palette;
const audienceTheme = muiCreateTheme(createTheme({ dark: false }), {
  palette: {
    primary: e.primary,
    error: e.error,
    red: e.red,
    fuchsia: e.fuchsia,
    yellow: e.yellow,
    blue: e.blue,
    green: e.green,
    purple: e.purple,
  },
});

function AudiencePage() {
  return (
    // injectFirst so emotion (MUI's engine) injects before the @tiny/components
    // @mui/styles (JSS) makeStyles rules, letting those custom styles win —
    // without it MUI's base styles override them and the components look broken.
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={audienceTheme}>
        <TinyApiProvider client={tinyApiClient}>
          <MessagesProvider>
            <RefreshProvider>
              <ConfirmProvider component={Confirm}>
                <AlbertHostProvider>
                  <Routes>
                    <Route index element={<AudienceHome />} />
                    <Route path="sources/all" element={<AudienceSourceAll />} />
                    <Route
                      path="sources/:segmentId"
                      element={<SourceDetailsRoute />}
                    />
                    <Route
                      path="suppression/:suppressionId"
                      element={<SuppressionDetailsRoute />}
                    />
                    <Route
                      path="edit-segments/:segmentId"
                      element={<EditSegmentRoute />}
                    />
                    <Route
                      path="segments/:segmentId"
                      element={<ViewSegmentRoute />}
                    />
                    <Route
                      path="*"
                      element={<Navigate to="/main/audience" replace />}
                    />
                  </Routes>
                </AlbertHostProvider>
              </ConfirmProvider>
            </RefreshProvider>
          </MessagesProvider>
        </TinyApiProvider>
      </ThemeProvider>
    </StyledEngineProvider>
  );
}

export default AudiencePage;
